// The core engine: given a normalized section and an instant, decide whether
// you can park there for FREE right now, and when that will next change.
//
// Design notes:
//  * All reasoning happens on the Prague wall clock (see time.js).
//  * Paid windows are weekly-recurring per ISO weekday. A window ending at
//    "24:00" that meets the next day's window starting at "00:00" is treated as
//    one continuous paid stretch across midnight (correct for 0-24 sections).
//  * Public holidays: since 1 May 2024 Prague charges ZPS on holidays like the
//    weekday they fall on (blue zones fully; purple/orange with a 20–80 Kč/24 h
//    price cap outside 7-day sections — see parking.praha.eu). The VPH
//    payAtHoliday flag is false city-wide and carries no information, so an
//    empty holidayRules falls back to the NORMAL rules; a non-empty set is
//    honoured as an explicit holiday regime.

import { pragueParts, isoWeekdayOf, addDays, hhmmToMinutes, minutesToHhmm, WEEKDAY_SHORT_CS, WEEKDAY_SHORT_EN } from './time.js'
import { isCzechHoliday, czechHolidayName } from './holidays.js'

const HORIZON_DAYS = 8 // enough to always find the next weekly transition

/**
 * Which rule set governs a given date. On public holidays an explicit non-empty
 * `holidayRules` wins; otherwise the NORMAL rules for that weekday apply —
 * Prague's official regime (since 1 May 2024) charges holidays like the day
 * they fall on, and treating the city's stale payAtHoliday=false as "free"
 * would send people into fines.
 */
function rulesetForDate(section, ymd) {
  const rules = section.rules || []
  if (!isCzechHoliday(ymd.year, ymd.month, ymd.day)) return rules
  const hr = section.holidayRules || []
  return hr.length > 0 ? hr : rules
}

/**
 * Paid intervals (minutes-of-day) applying on a specific calendar date.
 * Overnight windows (end<=start, e.g. 08:00–06:00) are expanded here: the
 * "head" [start..24:00] falls on the window's own weekday and the "tail"
 * [00:00..end] falls on the following morning. That keeps the stored rules
 * faithful to the source while still evaluating the wrap correctly.
 */
export function paidIntervalsForDate(section, ymd) {
  const wd = isoWeekdayOf(ymd.year, ymd.month, ymd.day)
  const intervals = []
  // Windows that start on this weekday.
  for (const r of rulesetForDate(section, ymd)) {
    if (!Array.isArray(r.days) || !r.days.includes(wd)) continue
    const start = hhmmToMinutes(r.start)
    const end = hhmmToMinutes(r.end)
    if (end > start) intervals.push({ start, end, rule: r })
    else if (end < start) intervals.push({ start, end: 1440, rule: r }) // head; tail spills to tomorrow
  }
  // Tails of yesterday's overnight windows spilling into this morning.
  const prev = addDays(ymd, -1)
  const pwd = isoWeekdayOf(prev.year, prev.month, prev.day)
  for (const r of rulesetForDate(section, prev)) {
    if (!Array.isArray(r.days) || !r.days.includes(pwd)) continue
    const start = hhmmToMinutes(r.start)
    const end = hhmmToMinutes(r.end)
    if (end < start && end > 0) intervals.push({ start: 0, end, rule: r }) // tail
  }
  intervals.sort((a, b) => a.start - b.start || a.end - b.end)
  return intervals
}

/**
 * Merge all paid windows over the next HORIZON_DAYS into absolute-minute ranges,
 * where 0 = midnight at the start of `startParts`'s day. Adjacent/overlapping
 * windows (including across midnight) are merged.
 */
function mergedPaidRanges(section, startParts) {
  const ranges = []
  let ymd = { year: startParts.year, month: startParts.month, day: startParts.day }
  for (let off = 0; off < HORIZON_DAYS; off++) {
    for (const iv of paidIntervalsForDate(section, ymd)) {
      ranges.push([off * 1440 + iv.start, off * 1440 + iv.end])
    }
    ymd = addDays(ymd, 1)
  }
  ranges.sort((a, b) => a[0] - b[0])
  const merged = []
  for (const r of ranges) {
    const last = merged[merged.length - 1]
    if (last && r[0] <= last[1]) last[1] = Math.max(last[1], r[1])
    else merged.push([r[0], r[1]])
  }
  return merged
}

/** Convert an absolute minute (relative to today's midnight) into a friendly label. */
function absToLabel(todayParts, absMin, lang = 'cs') {
  const dayOffset = Math.floor(absMin / 1440)
  const minute = absMin % 1440
  const date = addDays({ year: todayParts.year, month: todayParts.month, day: todayParts.day }, dayOffset)
  const isoWeekday = isoWeekdayOf(date.year, date.month, date.day)
  const names = lang === 'en' ? WEEKDAY_SHORT_EN : WEEKDAY_SHORT_CS
  return {
    dayOffset,
    minute,
    hhmm: minutesToHhmm(minute),
    isoWeekday,
    weekdayLabel: names[isoWeekday],
    date,
  }
}

/**
 * @typedef {Object} SectionStatus
 * @property {boolean} free
 * @property {('free'|'paid')} status
 * @property {boolean} holiday
 * @property {?string} holidayName
 * @property {?TariffRule} activeRule    the rule making it paid right now (if paid)
 * @property {?number} changesInMin      minutes until the free/paid state flips (null = never in horizon)
 * @property {?object} changesAt         friendly label of the next transition
 * @property {boolean} neverCharged      true if the section has no paid windows at all
 */

/**
 * Evaluate a single section at an instant.
 * @param {SectionProps} section
 * @param {Date} [refDate]
 * @param {('cs'|'en')} [lang]
 * @returns {SectionStatus}
 */
export function evaluateSection(section, refDate = new Date(), lang = 'cs') {
  const p = pragueParts(refDate)
  const nowAbs = p.minutesOfDay
  const holiday = isCzechHoliday(p.year, p.month, p.day)
  const holidayName = holiday ? czechHolidayName(p.year, p.month, p.day) : null

  const neverCharged = (section.rules || []).length === 0 && (section.holidayRules || []).length === 0

  const merged = mergedPaidRanges(section, p)

  // Is now inside a paid range?
  let current = null
  for (const [s, e] of merged) {
    if (nowAbs >= s && nowAbs < e) {
      current = [s, e]
      break
    }
  }

  if (current) {
    // Which of today's rules is active (for price / max-stay display)?
    const todays = paidIntervalsForDate(section, { year: p.year, month: p.month, day: p.day })
    const activeIv = todays.find((iv) => nowAbs >= iv.start && nowAbs < iv.end)
    return {
      free: false,
      status: 'paid',
      holiday,
      holidayName,
      activeRule: activeIv ? activeIv.rule : null,
      changesInMin: current[1] - nowAbs,
      changesAt: absToLabel(p, current[1], lang),
      neverCharged: false,
    }
  }

  // Free now — find the next paid start.
  const next = merged.find(([s]) => s > nowAbs)
  return {
    free: true,
    status: 'free',
    holiday,
    holidayName,
    activeRule: null,
    changesInMin: next ? next[0] - nowAbs : null,
    changesAt: next ? absToLabel(p, next[0], lang) : null,
    neverCharged,
  }
}

/** Convenience boolean. */
export function isFreeNow(section, refDate = new Date()) {
  return evaluateSection(section, refDate).free
}

/**
 * Format an arbitrary rule set into a compact schedule, e.g.
 * "Po–Pá 08:00–24:00 · So 08:00–18:00". Overnight windows (end<=start) get a
 * "+1" marker so "08:00–06:00 +1" reads as "until 06:00 next morning".
 * Returns '' for an empty rule set.
 */
export function summarizeRuleset(rules, lang = 'cs') {
  if (!rules || rules.length === 0) return ''
  const use = lang === 'en' ? WEEKDAY_SHORT_EN : WEEKDAY_SHORT_CS
  return rules
    .map((r) => {
      const days = [...r.days].sort((a, b) => a - b)
      const overnight = hhmmToMinutes(r.end) <= hhmmToMinutes(r.start)
      return `${formatDayList(days, use)} ${r.start}–${r.end}${overnight ? ' +1' : ''}`
    })
    .join(' · ')
}

/** Weekday schedule for a section (its normal `rules`). */
export function summarizeRules(section, lang = 'cs') {
  const rules = section.rules || []
  if (rules.length === 0) return lang === 'en' ? 'Never charged' : 'Nezpoplatněno'
  return summarizeRuleset(rules, lang)
}

/** True if two rule sets describe the same windows. */
export function rulesetsEqual(a = [], b = []) {
  const norm = (rs) =>
    JSON.stringify(
      rs
        .map((r) => `${[...r.days].sort((x, y) => x - y).join(',')}|${r.start}|${r.end}`)
        .sort()
    )
  return norm(a) === norm(b)
}

/**
 * Describe how a section behaves on public holidays.
 * @returns {{free:boolean, same:boolean, text:string}}
 */
export function holidayRegime(section, lang = 'cs') {
  const hr = section.holidayRules || []
  if (hr.length === 0) {
    // No explicit holiday windows -> the normal schedule of the day applies
    // (a holiday is charged like the weekday it falls on; it is NOT a Sunday).
    const never = (section.rules || []).length === 0
    if (never) return { free: true, same: true, text: lang === 'en' ? 'Free' : 'Zdarma' }
    return {
      free: false,
      same: true,
      text: lang === 'en' ? 'Charged as on a normal day' : 'Platí běžný rozvrh dne',
    }
  }
  if (rulesetsEqual(section.rules || [], hr)) {
    return {
      free: false,
      same: true,
      text: lang === 'en' ? 'Paid, same as weekdays' : 'Placeno, jako ve všední dny',
    }
  }
  return { free: false, same: false, text: summarizeRuleset(hr, lang) }
}

/** Format [1,2,3,4,5] -> "Po–Pá", [6,7] -> "So–Ne", [1,3] -> "Po, St". */
function formatDayList(days, names) {
  if (days.length === 0) return ''
  const runs = []
  let start = days[0]
  let prev = days[0]
  for (let i = 1; i < days.length; i++) {
    if (days[i] === prev + 1) {
      prev = days[i]
    } else {
      runs.push([start, prev])
      start = days[i]
      prev = days[i]
    }
  }
  runs.push([start, prev])
  return runs
    .map(([a, b]) => (a === b ? names[a] : `${names[a]}–${names[b]}`))
    .join(', ')
}
